import { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Dashboard from './Dashboard';
import PromptLibrary from './components/PromptLibrary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import AdminPanel from './AdminPanel';
import FirstLoginModal from './components/FirstLoginModal';
import { Lock, X } from 'lucide-react';
import { changePassword } from './services/database';

// Password Change Modal Component
const PasswordChangeModal: React.FC<{ isVisible: boolean; onClose: () => void }> = ({ isVisible, onClose }) => {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      // Change password using database
      const result = await changePassword(user!.id, newPassword, currentPassword);
      if (result.success) {
        setSuccess('Password updated successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(result.error || 'Failed to update password');
      }
    } catch (error) {
      setError('Error updating password');
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Change Password</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Password
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Enter current password"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Enter new password"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Confirm new password"
            />
          </div>
          
          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}
          
          {success && (
            <div className="text-green-600 text-sm">{success}</div>
          )}
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center space-x-2"
            >
              <Lock className="w-4 h-4" />
              <span>Update Password</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main App Component
const AppContent: React.FC = () => {
  const location = useLocation();
  const { user, isLoading, changeFirstLoginPassword } = useAuth();
  const [activeTab, setActiveTab] = useState<'gpts' | 'client-gpts' | 'prompts'>('gpts');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showFirstLoginModal, setShowFirstLoginModal] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    searchTerm: '',
    selectedCategory: '',
    selectedTags: [] as string[]
  });

  useEffect(() => {
    setFilters({
      searchTerm,
      selectedCategory,
      selectedTags
    });
  }, [searchTerm, selectedCategory, selectedTags]);

  // Check if this is the user's first login
  useEffect(() => {
    if (user && user.first_login) {
      setShowFirstLoginModal(true);
    }
  }, [user]);

  // Handle password reset routes (no auth required)
  if (location.pathname === '/forgot-password' || location.pathname === '/reset-password') {
    return (
      <Routes>
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Login key="login-component" />} />
      </Routes>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#1B2225] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login key="login-component" />} />
        <Route path="*" element={<Login key="login-component" />} />
      </Routes>
    );
  }

  return (
    <div className="min-h-screen bg-[#1B2225]">
      <Header 
        user={user}
        onTabChange={setActiveTab}
        activeTab={activeTab}
        onAdminClick={() => setShowAdminPanel(true)}
        onPasswordChange={() => setShowPasswordChange(true)}
      />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters Section */}
        <div className="bg-[#21282D] rounded-lg shadow-sm border border-gray-600 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">Search GPTs</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, description, or use cases..."
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-[#1B2225] text-gray-100 placeholder-gray-400"
              />
            </div>

            {/* Category Filter */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-100 mb-2">Category</label>
              <div className="relative">
                <button
                  onClick={() => setSelectedCategory(selectedCategory ? '' : 'placeholder')}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-left bg-[#1B2225] text-gray-100"
                >
                  {selectedCategory || 'All Categories'}
                </button>
                {selectedCategory && (
                  <div 
                    className="absolute z-10 w-full mt-1 bg-[#21282D] border border-gray-600 rounded-lg shadow-lg"
                    onMouseEnter={() => setSelectedCategory(selectedCategory)}
                    onMouseLeave={() => setSelectedCategory('')}
                  >
                    <div className="py-1">
                      <button
                        onClick={() => setSelectedCategory('')}
                        className="w-full px-3 py-2 text-left hover:bg-gray-700 text-gray-100"
                      >
                        All Categories
                      </button>
                      <button
                        onClick={() => setSelectedCategory('strategy')}
                        className="w-full px-3 py-2 text-left hover:bg-gray-700 text-gray-100"
                      >
                        Strategy & Planning
                      </button>
                      <button
                        onClick={() => setSelectedCategory('creative')}
                        className="w-full px-3 py-2 text-left hover:bg-gray-700 text-gray-100"
                      >
                        Creative & Messaging
                      </button>
                      <button
                        onClick={() => setSelectedCategory('data')}
                        className="w-full px-3 py-2 text-left hover:bg-gray-700 text-gray-100"
                      >
                        Data & Performance
                      </button>
                      <button
                        onClick={() => setSelectedCategory('client')}
                        className="w-full px-3 py-2 text-left hover:bg-gray-700 text-gray-100"
                      >
                        Client Communication
                      </button>
                      <button
                        onClick={() => setSelectedCategory('operations')}
                        className="w-full px-3 py-2 text-left hover:bg-gray-700 text-gray-100"
                      >
                        Operations & Productivity
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Tags Filter */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-100 mb-2">Tags</label>
              <div className="relative">
                <button
                  onClick={() => setSelectedTags(selectedTags.length > 0 ? [] : ['placeholder'])}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-left bg-[#1B2225] text-gray-100"
                >
                  {selectedTags.length > 0 ? `${selectedTags.length} selected` : 'All Tags'}
                </button>
                {selectedTags.length > 0 && (
                  <div 
                    className="absolute z-10 w-full mt-1 bg-[#21282D] border border-gray-600 rounded-lg shadow-lg"
                    onMouseEnter={() => setSelectedTags(selectedTags)}
                    onMouseLeave={() => setSelectedTags([])}
                  >
                    <div className="py-1 max-h-48 overflow-y-auto">
                      <button
                        onClick={() => setSelectedTags([])}
                        className="w-full px-3 py-2 text-left hover:bg-gray-700 text-gray-100"
                      >
                        All Tags
                      </button>
                      {['AI', 'Strategy', 'Creative', 'Data', 'Client', 'Operations', 'Planning', 'Analysis', 'Communication', 'Productivity'].map(tag => (
                        <button
                          key={tag}
                          onClick={() => {
                            if (selectedTags.includes(tag)) {
                              setSelectedTags(selectedTags.filter(t => t !== tag));
                            } else {
                              setSelectedTags([...selectedTags, tag]);
                            }
                          }}
                          className={`w-full px-3 py-2 text-left hover:bg-gray-700 text-gray-100 ${
                            selectedTags.includes(tag) ? 'bg-red-900 text-red-300' : ''
                          }`}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        {activeTab === 'gpts' ? (
          <Dashboard filters={filters} gptType="general" />
        ) : activeTab === 'client-gpts' ? (
          <Dashboard filters={filters} gptType="client" />
        ) : (
          <PromptLibrary />
        )}
      </main>

      {/* Admin Panel */}
      <AdminPanel 
        isVisible={showAdminPanel} 
        onClose={() => setShowAdminPanel(false)} 
      />

      {/* Password Change Modal */}
      <PasswordChangeModal 
        isVisible={showPasswordChange} 
        onClose={() => setShowPasswordChange(false)} 
      />

      {/* First Login Modal */}
      <FirstLoginModal
        isVisible={showFirstLoginModal}
        user={user}
        onPasswordChange={async (newPassword) => {
          const success = await changeFirstLoginPassword(newPassword);
          if (success) {
            setShowFirstLoginModal(false);
          }
        }}
      />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App; 